'use client';

import { useMemo } from 'react';
import type { EntityConfig } from '../types/app-config';
import { useRuntimeAuth } from './auth-context';

export type PermissionAction = 'list' | 'create' | 'read' | 'update' | 'delete' | string;

function normalizeAction(action: PermissionAction): PermissionAction {
  if (action === 'view') return 'read';
  return action;
}

export function resolvePermissionForAction(
  action: PermissionAction,
  entity?: EntityConfig | string,
): string {
  const normalizedAction = normalizeAction(action);

  if (!entity) {
    return normalizedAction;
  }

  if (typeof entity === 'string') {
    return `${entity}:${normalizedAction}`;
  }

  if (
    (normalizedAction === 'list'
      || normalizedAction === 'create'
      || normalizedAction === 'read'
      || normalizedAction === 'update'
      || normalizedAction === 'delete')
    && entity.permissions?.[normalizedAction]
  ) {
    return entity.permissions[normalizedAction] as string;
  }

  return `${entity.slug}:${normalizedAction}`;
}

export function usePermission(
  action: PermissionAction,
  entity?: EntityConfig | string,
): boolean {
  const { hasPermission } = useRuntimeAuth();

  const permission = useMemo(
    () => resolvePermissionForAction(action, entity),
    [action, entity],
  );

  return hasPermission(permission);
}
